/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { ReactNode, useCallback } from 'react';
import type { FormContext } from '@shared/types';
import FormSubmitBehavior from './FormSubmit';

type FormSubmitWithRecaptchaProps = {
  onSubmit: (formStore: FormContext) => void;
  onFallback?: (formStore: FormContext) => void;
  clearAfterSubmit?: boolean;
  noValidate?: boolean;
  recaptchaAction: string;
  children: ReactNode;
};

/**
 * FormSubmitWithRecaptcha component.
 * 
 * Wrapper around FormSubmitBehavior that automatically generates a reCAPTCHA token
 * before form submission. The token is added to the form values before calling onSubmit.
 * 
 * Falls back to normal form submission if reCAPTCHA is unavailable or fails.
 * 
 * @param {FormSubmitWithRecaptchaProps} props - Component props
 * @param {Function} props.onSubmit - Form submission handler
 * @param {Function} props.onFallback - Fallback handler for invalid forms
 * @param {boolean} props.clearAfterSubmit - Whether to clear form after submission
 * @param {boolean} props.noValidate - Whether to skip validation
 * @param {string} props.recaptchaAction - reCAPTCHA action name (e.g., 'form_submission')
 * @param {ReactNode} props.children - Child components (typically a submit button)
 * @returns {JSX.Element} Form submit behavior with reCAPTCHA protection
 * 
 * @example
 * ```tsx
 * <FormSubmitWithRecaptcha
 *   recaptchaAction="early_access_registration"
 *   onSubmit={(formStore) => {
 *     // Token is automatically sent as X-Anti-Bot header
 *     submitForm(formStore.formValues);
 *   }}
 * >
 *   <Button>Submit</Button>
 * </FormSubmitWithRecaptcha>
 * ```
 */
const FormSubmitWithRecaptcha: React.FC<FormSubmitWithRecaptchaProps> = ({
  onSubmit,
  onFallback,
  clearAfterSubmit = false,
  noValidate = false,
  recaptchaAction,
  children,
}) => {
  /**
   * Handle form submission.
   * Token generation is handled automatically by the HEADERS resolver in OpenAPI.ts
   * for every outgoing API request — no need to pre-generate here.
   */
  const handleSubmit = useCallback(
    (formStore: FormContext) => {
      onSubmit(formStore);
    },
    [onSubmit]
  );

  return (
    <FormSubmitBehavior
      onSubmit={handleSubmit}
      onFallback={onFallback}
      clearAfterSubmit={clearAfterSubmit}
      noValidate={noValidate}
    >
      {children}
    </FormSubmitBehavior>
  );
};

export default FormSubmitWithRecaptcha;
